import { useMutation } from '@apollo/client'
import { toast } from 'sonner'

import { UPDATE_STOP_ADDRESS, UPDATE_STOP_DETAILS } from '../../../../../../data/mutations/stops'
import type { IStop } from '../../../../../../types/shipment'
import { to24HourTime } from '../../../../../../utils/datetime-utils'
import { useStops } from '../contexts/use-stops-context'

type UpdateStopFieldsType = Partial<
  Pick<
    IStop,
    | 'instructions'
    | 'referenceNumber'
    | 'type'
    | 'appointment'
    | 'eta'
    | 'arrivalDateTime'
    | 'departureDateTime'
    | 'warehouse'
  >
>

/**
 * This hook is used to update the details of a stop.
 * This takes care of updating the reference number, instructions, type, appointment
 */
export const useUpdateStopDetails = (stop: IStop) => {
  const { shipment, stops, updateStops } = useStops()
  const [updateStopDetails] = useMutation(UPDATE_STOP_DETAILS, {
    onCompleted: () => {
      toast.success('Stop details updated successfully')
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  const [updateStopAddress] = useMutation(UPDATE_STOP_ADDRESS, {
    onCompleted: () => {
      toast.success('Stop warehouse updated successfully')
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  const handleUpdateStop = (fields: UpdateStopFieldsType) => {
    // Update local state immediately
    const updatedStop: IStop = {
      ...stop,
      ...fields
    }
    updateStops(stops.map((s) => (s.id === stop.id ? updatedStop : s)))

    toast.info('Updating stop details...')

    // Handle warehouse update separately if it's in the fields
    if (fields.warehouse) {
      if (fields.warehouse?.address && fields.warehouse?.locationID) {
        const warehouseInput = {
          address: fields.warehouse.address,
          locationID: fields.warehouse.locationID,
          shipmentID: shipment.id,
          shipmentOwnerEnterpriseWorkspaceID: shipment.shipmentOwnerWorkspace?.enterpriseWorkspaceID,
          shipmentOwnerWorkspaceID: shipment.shipmentOwnerWorkspace?.id,
          stopID: stop.id
        }
        updateStopAddress({ variables: warehouseInput })
      }
      return // Don't call updateStopDetails if only warehouse is being updated
    }

    // Prepare mutation input with all required fields (excluding warehouse)
    const input = {
      appointment: updatedStop.appointment && {
        end: updatedStop.appointment.end?.date
          ? {
              date: updatedStop.appointment.end.date,
              time: to24HourTime(updatedStop.appointment.end?.time) || null
            }
          : null,
        isFirstComeFirstServe: updatedStop.appointment.isFirstComeFirstServe,
        start: updatedStop.appointment.start?.date
          ? {
              date: updatedStop.appointment.start.date,
              time: to24HourTime(updatedStop.appointment.start?.time) || null
            }
          : null
      },
      instructions: updatedStop.instructions,
      referenceNumber: updatedStop.referenceNumber || null,
      shipmentID: shipment.id,
      stopID: stop.id,
      type: updatedStop.type || null
    }

    // Update server state
    updateStopDetails({
      variables: input
    })
  }

  return { handleUpdateStop, isShipmentOwner: shipment.isShipmentOwner }
}
